/**
 * Core Button Component
 * 
 * Unified button with variants, sizes, loading state, icon support, and optional RBAC gating.
 * Built on MUI Button with CVA variant management for consistent theming.
 */
import React from 'react';
import { Button as MuiButton, CircularProgress } from '@mui/material';
import { useAuth } from '@/context/useAuth';
import { hasPermission } from '@/utils/permissions';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'link';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  permission?: string;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const variantMap: Record<string, { muiVariant: 'contained' | 'outlined' | 'text'; color: 'primary' | 'error' | 'inherit' }> = {
  primary:   { muiVariant: 'contained', color: 'primary' },
  secondary: { muiVariant: 'outlined',  color: 'primary' },
  ghost:     { muiVariant: 'text',      color: 'inherit' },
  danger:    { muiVariant: 'contained', color: 'error' },
  outline:   { muiVariant: 'outlined',  color: 'inherit' },
  link:      { muiVariant: 'text',      color: 'primary' },
};

const sizeMap: Record<string, 'small' | 'medium' | 'large'> = {
  xs: 'small', sm: 'small', md: 'medium', lg: 'large', icon: 'small',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading = false, leftIcon, rightIcon, fullWidth, permission, children, disabled, className = '', ...props }, ref) => {
    const { user } = useAuth();

    // RBAC: hide button if permission is specified and not granted
    if (permission && !hasPermission(user, permission)) return null;

    const { muiVariant, color } = variantMap[variant] || variantMap.primary;
    const muiSize = sizeMap[size] || 'medium';

    return (
      <MuiButton
        ref={ref}
        variant={muiVariant}
        color={color}
        size={muiSize}
        disabled={disabled || loading}
        fullWidth={fullWidth}
        className={className}
        sx={{
          textTransform: 'none',
          minWidth: size === 'icon' ? 'auto' : undefined,
          px: size === 'icon' ? 1 : undefined,
          gap: 0.75,
          ...(variant === 'link' ? {
            textDecoration: 'underline',
            '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
          } : {}),
        }}
        {...props}
      >
        {loading ? (
          <CircularProgress size={16} color="inherit" sx={{ mr: children ? 1 : 0 }} />
        ) : leftIcon ? (
          <span className="flex items-center">{leftIcon}</span>
        ) : null}
        {children}
        {rightIcon && !loading && <span className="flex items-center">{rightIcon}</span>}
      </MuiButton>
    );
  }
);
Button.displayName = 'Button';
export default Button;
